var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useEffect, useRef, useCallback } from 'react';
import moduleStyles from './dance-ai-modal.module.scss';
import AccessibleDialog from '@cdo/apps/templates/AccessibleDialog';
import Button from '@cdo/apps/templates/Button';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@cdo/apps/util/reduxHooks';
import { closeAiModal } from '../danceRedux';
import classNames from 'classnames';
import { Workspace } from 'blockly/core';
import { chooseEffects, ChooseEffectsQuality, getGeneratedEffectScores, } from './DanceAiClient';
import DanceAiScore, { ScoreColors } from './DanceAiScore';
import AiVisualizationPreview from './AiVisualizationPreview';
import AiBlockPreview from './AiBlockPreview';
import { AiOutput, FieldKey, } from './types';
import { generateAiEffectBlocks, generateAiEffectBlocksFromResult, generatePreviewCode, getEmojiImageUrl, getLabelMap, getRangeArray, lerp, useInterval, } from './utils';
import color from '@cdo/apps/util/color';
var ToggleGroup = require('@cdo/apps/templates/ToggleGroup').default;
var i18n = require('../locale');
import inputLibraryJson from '@cdo/static/dance/ai/ai-inputs.json';
import aiBotMini from '@cdo/static/dance/ai/bot/ai-bot-mini.svg';
import aiBotHeadNormal from '@cdo/static/dance/ai/bot/ai-bot-head-normal.png';
import aiBotBodyNormal from '@cdo/static/dance/ai/bot/ai-bot-body-normal.png';
import aiBotHeadYes from '@cdo/static/dance/ai/bot/ai-bot-head-yes.png';
import aiBotBodyYes from '@cdo/static/dance/ai/bot/ai-bot-body-yes.png';
import aiBotBodyThink0 from '@cdo/static/dance/ai/bot/ai-bot-body-think0.png';
import aiBotBodyThink1 from '@cdo/static/dance/ai/bot/ai-bot-body-think1.png';
import aiBotBodyThink2 from '@cdo/static/dance/ai/bot/ai-bot-body-think2.png';
import analyticsReporter from '@cdo/apps/lib/util/AnalyticsReporter';
import { EVENTS } from '@cdo/apps/lib/util/AnalyticsConstants';
import ModalButton from './ModalButton';
export var Mode;
(function (Mode) {
    Mode["INITIAL"] = "initial";
    Mode["SELECT_INPUTS"] = "selectInputs";
    Mode["GENERATING"] = "generating";
    Mode["GENERATED"] = "generated";
    Mode["RESULTS"] = "results";
    Mode["EXPLANATION"] = "explanation";
})(Mode || (Mode = {}));
var Toggle;
(function (Toggle) {
    Toggle["EFFECT"] = "effect";
    Toggle["CODE"] = "code";
})(Toggle || (Toggle = {}));
var aiBotBodyThinkImages = [
    aiBotBodyThink0,
    aiBotBodyThink1,
    aiBotBodyThink2,
];
// How many emojis are to be selected.
var SLOT_COUNT = 3;
var DanceAiModal = function (_a) {
    var playSound = _a.playSound;
    var dispatch = useAppDispatch();
    // How many low-scoring results we show before the chosen one.
    var BAD_GENERATED_RESULTS_COUNT = 7;
    // How many substeps for each step in the generating mode.
    var GENERATING_SUBSTEP_COUNT = 3;
    // How long we spend in each substep in the generating mode.
    // Perform a linear interpolation from the first to the second, so
    // that we gradually speed up.
    var GENERATION_SUBSTEP_DURATION_MAX = 360;
    var GENERATION_SUBSTEP_DURATION_MIN = 360;
    // How many steps in the generated mode.
    var GENERATED_STEPS_COUNT = 3;
    // How long we spend in each step of the generated mode.
    var GENERATED_STEP_DURATION = 1500;
    // How many steps in the explanation mode.
    var EXPLANATION_STEPS_COUNT = 5;
    // How long we spend in each step of the explanation mode.
    var EXPLANATION_STEP_DURATION = 900;
    // The explanation shows the last (EXPLANATION_STEPS_COUNT-1) bad effects and the
    // one good effect.
    var EXPLANATION_START_INDEX = BAD_GENERATED_RESULTS_COUNT - EXPLANATION_STEPS_COUNT + 1;
    var generatedEffects = useRef({
        badEffects: [],
        goodEffect: undefined,
    });
    var minMaxAssociations = useRef({
        minIndividualScore: 0,
        maxTotalScore: 3 * SLOT_COUNT,
    });
    var _b = useState(Mode.INITIAL), mode = _b[0], setMode = _b[1];
    var _c = useState(0), currentInputSlot = _c[0], setCurrentInputSlot = _c[1];
    var _d = useState([]), inputs = _d[0], setInputs = _d[1];
    var _e = useState(0), inputAddCount = _e[0], setInputAddCount = _e[1];
    var _f = useState({
        step: 0,
        subStep: 0,
    }), generatingProgress = _f[0], setGeneratingProgress = _f[1];
    var _g = useState(0), generatedProgress = _g[0], setGeneratedProgress = _g[1];
    var _h = useState(Toggle.EFFECT), currentToggle = _h[0], setCurrentToggle = _h[1];
    var _j = useState(0), explanationProgress = _j[0], setExplanationProgress = _j[1];
    var currentAiModalField = useSelector(function (state) { return state.dance.currentAiModalField; });
    var aiModalOpenedFromFlyout = useSelector(function (state) { return state.dance.aiModalOpenedFromFlyout; });
    var aiOutput = useSelector(function (state) { return state.dance.aiOutput; });
    var getGeneratedEffect = useCallback(function (step) {
        if (step < BAD_GENERATED_RESULTS_COUNT) {
            return generatedEffects.current.badEffects[step];
        }
        else if (generatedEffects.current.goodEffect) {
            return generatedEffects.current.goodEffect;
        }
        else {
            return undefined;
        }
    }, []);
    var getScores = useCallback(function (useInputs, step) {
        var effect = getGeneratedEffect(step);
        if (effect) {
            return getGeneratedEffectScores(useInputs, effect);
        }
        return [0, 0, 0];
    }, [getGeneratedEffect]);
    // Calculates the minimum individual score
    // (ie, a SINGLE emoji association with a foreground/background palette combination),
    // and a maximum total score
    // (ie, the sum of ALL selected emoji's associations with a foreground/background palette combination).
    // Used to normalize and scale the data for easier differentiation between results by the user.
    var calculateMinMax = useCallback(function (useInputs) {
        // The minimum individual score is selected across all generated effects (bad and good).
        var minIndividualScore = getRangeArray(0, BAD_GENERATED_RESULTS_COUNT - 1).reduce(function (accumulator, currentValue) {
            var scores = getScores(useInputs, currentValue);
            var min = Math.min.apply(Math, scores);
            return min < accumulator ? min : accumulator;
        }, Infinity);
        // By definition, the maximum total score must come from the "good" effect.
        var goodEffectScores = getScores(useInputs, BAD_GENERATED_RESULTS_COUNT);
        var maxTotalScore = goodEffectScores.reduce(function (sum, score) { return (sum += score); });
        return { minIndividualScore: minIndividualScore, maxTotalScore: maxTotalScore };
    }, [getScores]);
    // Handle the case in which the modal is created with an existing value.
    useEffect(function () {
        if (mode === Mode.INITIAL) {
            var currentValueString = currentAiModalField === null || currentAiModalField === void 0 ? void 0 : currentAiModalField.getValue();
            if (currentValueString) {
                var currentValue_1 = JSON.parse(currentValueString);
                setMode(Mode.RESULTS);
                setInputs(currentValue_1.inputs);
                setGeneratingProgress({ step: BAD_GENERATED_RESULTS_COUNT, subStep: 0 });
                generatedEffects.current = {
                    badEffects: getRangeArray(0, BAD_GENERATED_RESULTS_COUNT - 1).map(function () { return chooseEffects(currentValue_1.inputs, ChooseEffectsQuality.BAD); }),
                    goodEffect: currentValue_1,
                };
                minMaxAssociations.current = calculateMinMax(currentValue_1.inputs);
            }
            else {
                setTimeout(function () {
                    setMode(Mode.SELECT_INPUTS);
                }, 500);
            }
        }
    }, [currentAiModalField, mode, calculateMinMax]);
    var getLabels = function () {
        var _a;
        var tempWorkspace = new Workspace();
        var blocksSvg = generateAiEffectBlocks(tempWorkspace);
        var foregroundLabels = getLabelMap(blocksSvg[0].getField('EFFECT'));
        var backgroundLabels = getLabelMap(blocksSvg[1].getField('EFFECT'));
        var paletteLabels = getLabelMap(blocksSvg[1].getField('PALETTE'));
        tempWorkspace.dispose();
        return _a = {},
            _a[FieldKey.FOREGROUND_EFFECT] = foregroundLabels,
            _a[FieldKey.BACKGROUND_EFFECT] = backgroundLabels,
            _a[FieldKey.BACKGROUND_PALETTE] = paletteLabels,
            _a;
    };
    var getAllItems = function () {
        return inputLibraryJson.items.map(function (item) {
            return __assign(__assign({}, item), { available: !inputs.includes(item.id) });
        });
    };
    var getItem = function (id) {
        return inputLibraryJson.items.find(function (item) { return item.id === id; });
    };
    var handleItemClick = function (id, available) {
        if (available) {
            // Add item inputs.
            if (currentInputSlot < SLOT_COUNT) {
                setInputs(__spreadArray(__spreadArray([], inputs, true), [id], false));
                setCurrentInputSlot(currentInputSlot + 1);
                setInputAddCount(inputAddCount + 1);
                playSound('ai-select-emoji');
            }
        }
        else {
            // Remove item from inputs.
            setInputs(inputs.filter(function (input) { return input !== id; }));
            setCurrentInputSlot(currentInputSlot - 1);
            playSound('ai-deselect-emoji');
        }
    };
    var handleGenerateClick = function () {
        startAi();
        analyticsReporter.sendEvent(EVENTS.DANCE_PARTY_AI_BACKGROUND_GENERATED, {
            emojis: inputs,
        });
        setMode(Mode.GENERATING);
    };
    var handleStartOverClick = function () {
        analyticsReporter.sendEvent(EVENTS.DANCE_PARTY_AI_BACKGROUND_RESTARTED, {
            emojis: inputs,
        });
        setInputs([]);
        setCurrentInputSlot(0);
        setGeneratingProgress({ step: 0, subStep: 0 });
        setGeneratedProgress(0);
        setMode(Mode.SELECT_INPUTS);
    };
    var handleRegenerateClick = function () {
        analyticsReporter.sendEvent(EVENTS.DANCE_PARTY_AI_BACKGROUND_REGENERATED, {
            emojis: inputs,
        });
        setGeneratingProgress({ step: 0, subStep: 0 });
        setGeneratedProgress(0);
        setCurrentToggle(Toggle.EFFECT);
        handleGenerateClick();
    };
    var handleExplanationClick = function () {
        analyticsReporter.sendEvent(EVENTS.DANCE_PARTY_AI_BACKGROUND_EXPLAINED, {
            emojis: inputs,
        });
        setGeneratedProgress(0);
        setExplanationProgress(0);
        setMode(Mode.EXPLANATION);
    };
    var handleUseClick = function () {
        if (!generatedEffects.current.goodEffect) {
            // Effect should exist when Use is clicked
            return;
        }
        analyticsReporter.sendEvent(EVENTS.DANCE_PARTY_AI_BACKGROUND_USED, __assign({ emojis: inputs }, generatedEffects.current.goodEffect));
        var currentValue = __assign({ inputs: inputs }, generatedEffects.current.goodEffect);
        currentAiModalField === null || currentAiModalField === void 0 ? void 0 : currentAiModalField.setValue(JSON.stringify(currentValue));
        onClose();
    };
    var handleLeaveExplanation = function () {
        setMode(Mode.RESULTS);
    };
    // One-shot update of step & substep.
    var updateGeneratingProgress = function (progress) {
        // Do a deep copy into a new object to ensure that a re-render
        // is triggered at the end of this work.
        var currentProgress = __assign({}, progress);
        if (currentProgress.subStep < GENERATING_SUBSTEP_COUNT - 1) {
            // Bump substep.
            currentProgress.subStep++;
        }
        else if (currentProgress.step < BAD_GENERATED_RESULTS_COUNT) {
            // Bump step and reset substep.
            currentProgress.step++;
            currentProgress.subStep = 0;
        }
        return currentProgress;
    };
    // Handle moments when we should play a sound or do another action.
    useEffect(function () {
        if (mode === Mode.GENERATING && generatingProgress.subStep === 0) {
            if (generatingProgress.step < BAD_GENERATED_RESULTS_COUNT) {
                playSound('ai-generate-no', { volume: 0.2 });
            }
            else {
                setMode(Mode.GENERATED);
            }
        }
        else if (mode === Mode.GENERATED) {
            if (generatedProgress === 0) {
                playSound('ai-generate-yes', { volume: 0.2 });
            }
            else if (generatedProgress === GENERATED_STEPS_COUNT - 1) {
                setMode(Mode.RESULTS);
            }
        }
        else if (mode === Mode.EXPLANATION) {
            if (explanationProgress < EXPLANATION_STEPS_COUNT - 1) {
                playSound('ai-generate-no', { volume: 0.2 });
            }
            else {
                playSound('ai-generate-yes', { volume: 0.2 });
            }
        }
    }, [
        generatingProgress,
        generatedProgress,
        explanationProgress,
        mode,
        playSound,
    ]);
    var getGeneratingStepDuration = function () {
        return lerp(generatingProgress.step, BAD_GENERATED_RESULTS_COUNT, GENERATION_SUBSTEP_DURATION_MAX, GENERATION_SUBSTEP_DURATION_MIN);
    };
    // Animate through the generating or explanation process.
    useInterval(function () {
        if (mode === Mode.GENERATING) {
            setGeneratingProgress(updateGeneratingProgress);
        }
        else if (mode === Mode.GENERATED) {
            if (generatedProgress < GENERATED_STEPS_COUNT - 1) {
                setGeneratedProgress(function (progress) { return progress + 1; });
            }
        }
        else if (mode === Mode.EXPLANATION) {
            if (explanationProgress < EXPLANATION_STEPS_COUNT - 1) {
                setExplanationProgress(function (progress) { return progress + 1; });
            }
        }
    }, mode === Mode.GENERATING
        ? getGeneratingStepDuration()
        : mode === Mode.GENERATED
            ? GENERATED_STEP_DURATION
            : mode === Mode.EXPLANATION
                ? EXPLANATION_STEP_DURATION
                : undefined);
    var startAi = function () {
        generatedEffects.current = {
            badEffects: getRangeArray(0, BAD_GENERATED_RESULTS_COUNT - 1).map(function () {
                return chooseEffects(inputs, ChooseEffectsQuality.BAD);
            }),
            goodEffect: chooseEffects(inputs, ChooseEffectsQuality.GOOD),
        };
        minMaxAssociations.current = calculateMinMax(inputs);
    };
    var handleConvertBlocks = function () {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        analyticsReporter.sendEvent(EVENTS.DANCE_PARTY_AI_BACKGROUND_EDITED, __assign({ emojis: inputs }, generatedEffects.current.goodEffect));
        if (!generatedEffects.current.goodEffect) {
            return;
        }
        var blocksSvg = generateAiEffectBlocksFromResult(Blockly.getMainWorkspace(), generatedEffects.current.goodEffect);
        var origBlock = currentAiModalField === null || currentAiModalField === void 0 ? void 0 : currentAiModalField.getSourceBlock();
        if (origBlock && currentAiModalField) {
            if (!origBlock.getPreviousBlock()) {
                // This block isn't attached to anything at all.
                var blockXY = origBlock.getRelativeToSurfaceXY();
                blocksSvg[0].moveTo(blockXY);
            }
            else if (!((_a = origBlock === null || origBlock === void 0 ? void 0 : origBlock.getPreviousBlock()) === null || _a === void 0 ? void 0 : _a.nextConnection)) {
                // origBlock is the first input (for example, to a setup block),
                // without a regular code block above it.
                (_d = (_c = (_b = origBlock === null || origBlock === void 0 ? void 0 : origBlock.getPreviousBlock()) === null || _b === void 0 ? void 0 : _b.getInput('DO')) === null || _c === void 0 ? void 0 : _c.connection) === null || _d === void 0 ? void 0 : _d.connect(blocksSvg[0].previousConnection);
            }
            else {
                // origBlock has a regular block above it.
                (_f = (_e = origBlock === null || origBlock === void 0 ? void 0 : origBlock.getPreviousBlock()) === null || _e === void 0 ? void 0 : _e.nextConnection) === null || _f === void 0 ? void 0 : _f.connect(blocksSvg[0].previousConnection);
            }
            (_h = (_g = origBlock === null || origBlock === void 0 ? void 0 : origBlock.getNextBlock()) === null || _g === void 0 ? void 0 : _g.previousConnection) === null || _h === void 0 ? void 0 : _h.connect(blocksSvg[1].nextConnection);
            blocksSvg.forEach(function (blockSvg) {
                blockSvg.initSvg();
                blockSvg.render();
            });
            origBlock.dispose(false);
            // End modal.
            onClose();
        }
    };
    var getPreviewCode = function (currentGeneratedEffect) {
        if (!currentGeneratedEffect) {
            return '';
        }
        var tempWorkspace = new Workspace();
        var previewCode = generatePreviewCode(tempWorkspace, currentGeneratedEffect);
        tempWorkspace.dispose();
        return previewCode;
    };
    var onClose = function () { return dispatch(closeAiModal()); };
    var showUseButton = mode === Mode.RESULTS &&
        currentToggle === Toggle.EFFECT &&
        (aiOutput === AiOutput.AI_BLOCK || aiOutput === AiOutput.BOTH);
    var showConvertButton = mode === Mode.RESULTS &&
        currentToggle === Toggle.CODE &&
        (aiOutput === AiOutput.GENERATED_BLOCKS || aiOutput === AiOutput.BOTH);
    var aiBotHead = aiBotHeadNormal;
    var aiBotBody = aiBotBodyNormal;
    var previewAreaClass = undefined;
    if (mode === Mode.GENERATED ||
        (mode === Mode.GENERATING &&
            generatingProgress.step >= BAD_GENERATED_RESULTS_COUNT)) {
        aiBotHead = aiBotHeadYes;
        aiBotBody = aiBotBodyYes;
        previewAreaClass = moduleStyles.previewAreaYes;
    }
    else if (mode === Mode.GENERATING) {
        aiBotBody =
            aiBotBodyThinkImages[generatingProgress.step % aiBotBodyThinkImages.length];
    }
    var explanationKeyDotColor = [
        moduleStyles.dotFirst,
        moduleStyles.dotSecond,
        moduleStyles.dotThird,
    ];
    var headerValue = function () {
        return (React.createElement("div", { className: moduleStyles.inputsContainer, tabIndex: 0, onClick: handleStartOverClick }, getRangeArray(0, SLOT_COUNT - 1).map(function (index) {
            var item = getItem(inputs[index]);
            return (React.createElement("div", { key: index, className: moduleStyles.emojiSlot }, item && (React.createElement(EmojiIcon, { item: item, className: moduleStyles.emojiSlotIcon }))));
        })));
    };
    // The header comes from a localized string like this: "generate {input} effect".
    // We split the localized string on the "{input}", and rebuild the HTML but with
    // the emoji box in place of that "{input}".
    var INPUT_KEY = '__CDO_INPUTS__';
    var headerTextSplit = i18n
        .danceAiModalHeading({ input: INPUT_KEY })
        .split(INPUT_KEY);
    var headerContent = [
        headerTextSplit[0],
        headerValue(),
        headerTextSplit[1],
    ].map(function (part, index) {
        return React.createElement("span", { key: index }, part);
    });
    var currentGeneratedEffect = getGeneratedEffect(generatingProgress.step);
    var lastInputItem = currentInputSlot > 0 ? getItem(inputs[currentInputSlot - 1]) : undefined;
    // Visualization preview size, in pixels.
    var previewSize = 280;
    var previewSizeSmall = 90;
    var labels = getLabels();
    // While generating, we render two previews at a time, so that as a new
    // one appears, it will smoothly fade in over the top of the previous one.
    var indexesToPreview = [];
    if (mode === Mode.GENERATING || mode === Mode.GENERATED) {
        if (generatingProgress.step > 0) {
            indexesToPreview.push(generatingProgress.step - 1);
        }
        indexesToPreview.push(generatingProgress.step);
    }
    else if (mode === Mode.RESULTS) {
        indexesToPreview.push(BAD_GENERATED_RESULTS_COUNT);
    }
    // How long the preview takes to appear.
    var previewAppearDuration = getGeneratingStepDuration() / 2.5;
    var text = mode === Mode.SELECT_INPUTS
        ? i18n.danceAiModalChooseEmoji()
        : mode === Mode.GENERATING
            ? i18n.danceAiModalFinding2()
            : mode === Mode.GENERATED
                ? i18n.danceAiModalGenerating2()
                : mode === Mode.RESULTS && currentToggle === Toggle.EFFECT
                    ? i18n.danceAiModalEffect2()
                    : mode === Mode.RESULTS && currentToggle === Toggle.CODE
                        ? i18n.danceAiModalCode2()
                        : mode === Mode.EXPLANATION
                            ? i18n.danceAiModalExplanation2()
                            : undefined;
    return (React.createElement(AccessibleDialog, { className: moduleStyles.dialog, onClose: onClose, initialFocus: false, styles: { modalBackdrop: moduleStyles.modalBackdrop } },
        React.createElement("div", { id: "ai-modal-header-area", className: moduleStyles.headerArea },
            React.createElement("div", { className: moduleStyles.headerAreaLeft },
                React.createElement("img", { src: aiBotMini, className: moduleStyles.botImage, alt: i18n.danceAiModalBotAlt() }),
                headerContent),
            React.createElement("div", { id: "ai-modal-header-area-right", className: moduleStyles.headerAreaRight },
                React.createElement("button", { className: moduleStyles.closeButton, "data-dismiss": "modal", type: "button", onClick: onClose },
                    React.createElement("i", { className: "fa fa-close", "aria-hidden": true }),
                    React.createElement("span", { className: "sr-only" }, i18n.danceAiModalClose())))),
        React.createElement("div", { id: "ai-modal-inner-area", className: moduleStyles.innerArea },
            mode === Mode.RESULTS && (React.createElement("div", { id: "toggle-area", className: moduleStyles.toggleArea, style: { zIndex: mode === Mode.RESULTS ? 1 : 0 } },
                React.createElement(ToggleGroup, { selected: currentToggle, activeColor: color['light_primary_500'], onChange: function (value) {
                        setCurrentToggle(value);
                    }, useRebrandedLikeStyles: true },
                    React.createElement("button", { key: 0, type: "button", value: Toggle.EFFECT }, i18n.danceAiModalEffectButton()),
                    React.createElement("button", { key: 1, type: "button", value: Toggle.CODE }, i18n.danceAiModalCodeButton())))),
            text && (React.createElement("div", { id: "text-area", className: moduleStyles.textArea },
                React.createElement("div", { key: text, className: moduleStyles.text }, text))),
            React.createElement("div", { id: "inputs-area", className: moduleStyles.inputsArea, style: { zIndex: mode === Mode.SELECT_INPUTS ? 1 : 0 } }, mode === Mode.SELECT_INPUTS && (React.createElement("div", { id: "inputs-container", className: moduleStyles.inputsContainer }, getAllItems().map(function (item, index) {
                return (React.createElement(EmojiIcon, { key: index, item: item, onClick: function () { return handleItemClick(item.id, item.available); }, isHighlighted: !item.available }));
            })))),
            React.createElement("div", { id: "bot-area", className: moduleStyles.botArea },
                mode === Mode.SELECT_INPUTS && lastInputItem && (React.createElement("div", { key: inputAddCount, id: "flying-emoji", className: moduleStyles.flyingEmoji },
                    React.createElement(EmojiIcon, { item: lastInputItem, className: moduleStyles.emojiSlotIcon }))),
                (mode === Mode.INITIAL ||
                    mode === Mode.SELECT_INPUTS ||
                    mode === Mode.GENERATING ||
                    mode === Mode.GENERATED ||
                    mode === Mode.RESULTS) && (React.createElement("div", { className: moduleStyles.botContainer },
                    React.createElement("img", { src: aiBotHead, className: classNames(moduleStyles.botHead, mode === Mode.SELECT_INPUTS &&
                            currentInputSlot < SLOT_COUNT &&
                            moduleStyles.botHeadOpen), alt: i18n.danceAiModalBotAlt() }),
                    React.createElement("img", { src: aiBotBody, className: moduleStyles.botBody, alt: i18n.danceAiModalBotAlt() })))),
            (mode === Mode.GENERATING ||
                mode === Mode.GENERATED ||
                mode === Mode.RESULTS) && (React.createElement("div", { id: "preview-area", className: classNames(moduleStyles.previewArea, previewAreaClass) },
                React.createElement("div", { id: "flip-card", className: moduleStyles.flipCard },
                    React.createElement("div", { id: "flip-card-inner", className: classNames(moduleStyles.flipCardInner, mode === Mode.RESULTS &&
                            currentToggle === Toggle.CODE &&
                            moduleStyles.flipCardInnerFlipped) },
                        React.createElement("div", { id: "flip-card-front", className: moduleStyles.flipCardFront }, indexesToPreview.map(function (index) {
                            return (React.createElement("div", { id: 'preview-container-' + index, key: 'preview-container-' + index, className: moduleStyles.previewContainer, style: {
                                    animationDuration: previewAppearDuration + 'ms',
                                } },
                                React.createElement(AiVisualizationPreview, { id: 'ai-preview-' + index, code: getPreviewCode(getGeneratedEffect(index)), size: previewSize })));
                        })),
                        React.createElement("div", { id: "flip-card-back", className: moduleStyles.flipCardBack }, mode === Mode.RESULTS && (React.createElement("div", { id: "block-preview", className: moduleStyles.blockPreview }, currentGeneratedEffect && (React.createElement(AiBlockPreview, { results: currentGeneratedEffect }))))))))),
            mode === Mode.GENERATED && (React.createElement("div", { id: "check-area", className: moduleStyles.checkArea },
                React.createElement("i", { className: "fa fa-check-circle" }))),
            mode === Mode.EXPLANATION && currentGeneratedEffect && (React.createElement("div", { id: "explanation-area", className: moduleStyles.explanationArea },
                React.createElement("div", { className: moduleStyles.key }, getRangeArray(0, SLOT_COUNT - 1).map(function (index) {
                    var item = getItem(inputs[index]);
                    return (React.createElement("div", { key: index, className: moduleStyles.emojiSlot },
                        React.createElement("div", { className: classNames(moduleStyles.dot, explanationKeyDotColor[index]) }),
                        item && (React.createElement(EmojiIcon, { item: item, className: moduleStyles.emojiSlotIcon }))));
                })),
                React.createElement("div", { className: moduleStyles.visualizationContainer }, getRangeArray(EXPLANATION_START_INDEX, EXPLANATION_START_INDEX + explanationProgress).map(function (index) {
                    var _a, _b, _c;
                    return (React.createElement("div", { key: index, className: moduleStyles.visualizationColumn },
                        React.createElement(DanceAiScore, { scores: getScores(inputs, index), minMax: minMaxAssociations.current, colors: index < BAD_GENERATED_RESULTS_COUNT
                                ? ScoreColors.NORMAL_NO
                                : ScoreColors.NORMAL_YES, slotCount: SLOT_COUNT }),
                        React.createElement("div", { title: labels.backgroundEffect[((_a = getGeneratedEffect(index)) === null || _a === void 0 ? void 0 : _a.backgroundEffect) || 0] +
                                ' - ' +
                                labels.foregroundEffect[((_b = getGeneratedEffect(index)) === null || _b === void 0 ? void 0 : _b.foregroundEffect) || 0] +
                                ' - ' +
                                labels.backgroundColor[((_c = getGeneratedEffect(index)) === null || _c === void 0 ? void 0 : _c.backgroundColor) || 0] },
                            React.createElement(AiVisualizationPreview, { id: 'ai-preview-' + index, code: getPreviewCode(getGeneratedEffect(index)), size: previewSizeSmall, durationMs: index < BAD_GENERATED_RESULTS_COUNT
                                    ? EXPLANATION_STEP_DURATION
                                    : undefined }))));
                })))),
            React.createElement("div", { id: "buttons-area-top", className: moduleStyles.buttonsAreaTop },
                React.createElement("div", { id: "buttons-area-top-left" },
                    React.createElement(ModalButton, { currentMode: mode, showFor: [Mode.RESULTS], id: "start-over-button", onClick: handleStartOverClick, color: Button.ButtonColor.neutralDark, className: classNames(moduleStyles.button, moduleStyles.buttonWithoutText), iconClassName: moduleStyles.buttonIcon, "aria-label": i18n.danceAiModalStartOverButton(), title: i18n.danceAiModalStartOverButton(), icon: "fast-backward" })),
                React.createElement("div", { id: "buttons-area-top-right" },
                    React.createElement(ModalButton, { currentMode: mode, showFor: [Mode.RESULTS], id: "explanation-button", onClick: handleExplanationClick, color: Button.ButtonColor.neutralDark, "aria-label": i18n.danceAiModalExplanationButton(), title: i18n.danceAiModalExplanationButton(), className: classNames(moduleStyles.button, moduleStyles.buttonWithoutText), iconClassName: moduleStyles.buttonIcon, icon: "chart-simple" }),
                    React.createElement(ModalButton, { currentMode: mode, showFor: [Mode.EXPLANATION], id: "leave-explanation-button", onClick: handleLeaveExplanation, color: Button.ButtonColor.brandSecondaryDefault, "aria-label": i18n.danceAiModalBack(), title: i18n.danceAiModalBack(), className: classNames(moduleStyles.button, moduleStyles.buttonWithoutText), iconClassName: moduleStyles.buttonIcon, icon: "chart-simple" }))),
            React.createElement("div", { id: "buttons-area", className: moduleStyles.buttonsArea },
                React.createElement("div", null,
                    React.createElement(ModalButton, { currentMode: mode, showFor: [Mode.RESULTS], id: "regenerate-button", onClick: handleRegenerateClick, color: Button.ButtonColor.neutralDark, className: moduleStyles.button, icon: "refresh", iconClassName: classNames(moduleStyles.buttonIcon, moduleStyles.buttonIconWithText), text: i18n.danceAiModalRegenerateButton() })),
                React.createElement("div", null,
                    currentInputSlot >= SLOT_COUNT && (React.createElement(ModalButton, { currentMode: mode, showFor: [Mode.SELECT_INPUTS], id: "generate-button", text: i18n.danceAiModalGenerateButton(), onClick: handleGenerateClick, color: Button.ButtonColor.brandSecondaryDefault, className: moduleStyles.button, useDefaultLineHeight: true })),
                    showConvertButton && (React.createElement(Button, { id: "convert-button", text: i18n.danceAiModalUseCodeButton(), onClick: handleConvertBlocks, color: Button.ButtonColor.brandSecondaryDefault, className: moduleStyles.button, disabled: aiModalOpenedFromFlyout, useDefaultLineHeight: true })),
                    showUseButton && (React.createElement(Button, { id: "use-button", text: i18n.danceAiModalUseEffectButton(), onClick: handleUseClick, color: Button.ButtonColor.brandSecondaryDefault, className: moduleStyles.button, useDefaultLineHeight: true })))))));
};
var EmojiIcon = function (_a) {
    var item = _a.item, onClick = _a.onClick, className = _a.className, isHighlighted = _a.isHighlighted;
    var isButton = onClick !== undefined;
    var Tag = isButton ? 'button' : 'div';
    return (React.createElement(Tag, { type: isButton ? 'button' : undefined, key: item.id, onClick: onClick, style: {
            backgroundImage: "url(".concat(getEmojiImageUrl(item.id), ")"),
        }, className: classNames(moduleStyles.emojiIcon, isButton && moduleStyles.emojiIconButton, isHighlighted && moduleStyles.emojiIconButtonHighlighted, className), "aria-label": item.emoji }));
};
export default DanceAiModal;
